import { useMemo } from 'react';
import type { UseResourceCustomContext } from '@atlassian/jira-resource-with-custom-router-context/src/controllers/types.tsx';
import { useResourceWithCustomRouterContext } from '@atlassian/jira-resource-with-custom-router-context/src/controllers/use-resource-with-custom-router-context/index.tsx';
import { requiredFrontendProperties } from '@atlassian/jira-router-resources-service-desk-project-getting-started/src/services/index.tsx';
import { INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY } from '@atlassian/jira-common-constants/src/project-template-type-keys.tsx';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import {
	GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_ITSM,
	GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_PROD,
} from '../../constants.tsx';
import type { ExcludedGettingStartedPageTemplatesType } from '../../types.tsx';
import { useIsFirstProjectITSM } from '../first-project-itsm/useIsFirstProjectITSM.tsx';

const useInitialProjectTemplateProjectPropertyKeyWithContext = (
	customContext?: UseResourceCustomContext,
) => {
	const { data, loading } = useResourceWithCustomRouterContext(
		requiredFrontendProperties,
		customContext,
	);
	if (!data) {
		return [null, loading];
	}
	return [data[INITIAL_PROJECT_TEMPLATE_PROJECT_PROPERTY_KEY], loading];
};

export const useIsGettingStartedPageExcludedProject = (
	projectId?: string,
	customContext?: UseResourceCustomContext,
) => {
	const [initialProjectTemplate, isLoading] =
		useInitialProjectTemplateProjectPropertyKeyWithContext(customContext);

	const {
		isLoading: isFirstProjectITSMLoading,
		isFirstProject,
		isFirstProjectItsm,
	} = useIsFirstProjectITSM(projectId);
	const excludedProjectTemplates =
		isFirstProject &&
		isFirstProjectItsm &&
		expValEquals('jsm_getting_started_page_for_nple_exp', 'cohort', 'variation')
			? GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_ITSM
			: GETTING_STARTED_PAGE_EXCLUDED_PROJECT_TEMPLATES_FOR_PROD;

	return useMemo(
		() => [
			isLoading || isFirstProjectITSMLoading,
			excludedProjectTemplates.includes(
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				initialProjectTemplate as ExcludedGettingStartedPageTemplatesType,
			),
		],
		[excludedProjectTemplates, initialProjectTemplate, isFirstProjectITSMLoading, isLoading],
	);
};
